// 업무보고 / 결재
//
// store/index.js 가 이 조각들을 펼쳐 하나의 루트 스토어로 합친다.
// Vuex 의 modules 옵션이 아니라 "조각"인 이유: Vuex 는 namespaced 여부와 무관하게
// 모듈 state 를 모듈 이름 아래에 중첩시킨다. 진짜 모듈로 바꾸면 store.state.tasks 가
// store.state.tasks.tasks 가 되어 소비자 40곳을 전부 고쳐야 한다.
// 조각 합성은 동작이 100% 동일하면서 파일만 나눈다.
//
// 액션은 실패를 그대로 던진다. 호출부가 전부 자기 문맥의 메시지로 ElMessage 를
// 띄우고 있어서, 예전처럼 여기서 SET_LOADING/SET_ERROR 를 커밋해도 그 값을 읽는
// 화면은 없었다.

import http from '@/utils/http'

export const state = {
  reports: [],
  reportPagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
  currentReport: null,
  // 내가 결재할 **보고서** 목록. /api/reports/dashboard/pending-approvals 가 준다.
  pendingApprovals: [],
  /*
   * 좌측 레일 · 하단 탭 배지가 쓰는 숫자.
   * /api/approvals/count 의 counts.total 이며 **보고서 + 업무 완료요청 합계**다.
   * pendingApprovals.length(보고서만)와 다른 값이고, 달라야 맞다.
   */
  pendingApprovalCount: 0,
  myReportSummary: null
}

export const mutations = {
  SET_REPORTS(state, { reports, pagination }) {
    state.reports = reports
    if (pagination) state.reportPagination = pagination
  },

  SET_CURRENT_REPORT(state, report) {
    state.currentReport = report
  },

  SET_PENDING_APPROVALS(state, items) {
    /*
     * **pendingApprovalCount 를 여기서 덮지 않는다.**
     *
     * 예전에는 `state.pendingApprovalCount = items.length` 였다. 그런데 이 값은
     * fetchApprovalCount 가 /api/approvals/count 에서 받아 오는 **보고서 + 업무
     * 완료요청 합계**이기도 하다. 두 액션이 같은 필드를 쓰니 나중에 실행된 쪽이 이겼다:
     *   - 홈 진입      → fetchPendingApprovals 가 마지막 → 배지가 보고서 수만 표시
     *   - 30초 뒤 폴링 → fetchApprovalCount 가 마지막   → 배지가 합계로 바뀜
     * 아무도 건드리지 않았는데 좌측 레일 숫자가 3에서 5로 뛰는 것처럼 보였다.
     */
    state.pendingApprovals = items
  },

  SET_PENDING_APPROVAL_COUNT(state, count) {
    state.pendingApprovalCount = count
  },

  SET_MY_REPORT_SUMMARY(state, summary) {
    state.myReportSummary = summary
  }
}

/**
 * 승인 · 반려 공통. 처리한 문서를 currentReport 로 두고, 홈 카드 목록과 배지를 다시 센다.
 * 배지 갱신을 호출부(상세 · 결재함 · 홈)에 맡기면 한 곳만 빠져도 처리한 문서가 배지에 남는다.
 */
async function decide({ commit, dispatch }, action, { id, comment }) {
  const response = await http.post(`/api/reports/${id}/${action}`, { comment })
  commit('SET_CURRENT_REPORT', response.data.report)
  await dispatch('fetchPendingApprovals')
  dispatch('fetchApprovalCount').catch(() => {})
  return response.data.report
}

export const actions = {
  /**
   * 통합 결재 카운트 (보고서 + 업무 완료요청).
   * 홈 카드는 fetchPendingApprovals 의 목록을 쓰고, 배지는 이 가벼운 COUNT 로 갱신한다.
   */
  async fetchApprovalCount({ commit }) {
    const response = await http.get('/api/approvals/count')
    commit('SET_PENDING_APPROVAL_COUNT', response.data.counts.total)
    return response.data.counts
  },

  async fetchReports({ commit }, filters = {}) {
    const response = await http.get('/api/reports', { params: filters })
    commit('SET_REPORTS', { reports: response.data.reports || [], pagination: response.data.pagination })
    return response.data
  },

  async fetchReportCalendar(_, params = {}) {
    const response = await http.get('/api/reports/calendar', { params })
    return response.data.reports || []
  },

  async fetchReport({ commit }, id) {
    const response = await http.get(`/api/reports/${id}`)
    commit('SET_CURRENT_REPORT', response.data.report)
    return response.data.report
  },

  async createReport(_, data) {
    const response = await http.post('/api/reports', data)
    return response.data.report
  },

  async updateReport({ commit }, { id, data }) {
    const response = await http.put(`/api/reports/${id}`, data)
    commit('SET_CURRENT_REPORT', response.data.report)
    return response.data.report
  },

  async deleteReport(_, id) {
    await http.delete(`/api/reports/${id}`)
  },

  async submitReport({ commit }, { id, approverIds }) {
    const response = await http.post(`/api/reports/${id}/submit`, { approverIds })
    commit('SET_CURRENT_REPORT', response.data.report)
    return response.data.report
  },

  approveReport(ctx, payload) {
    return decide(ctx, 'approve', payload)
  },

  rejectReport(ctx, payload) {
    return decide(ctx, 'reject', payload)
  },

  async cancelReport({ commit }, { id, comment }) {
    const response = await http.post(`/api/reports/${id}/cancel`, { comment })
    commit('SET_CURRENT_REPORT', response.data.report)
    return response.data.report
  },

  async uploadReportAttachment(_, { reportId, file, onProgress }) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await http.post(`/api/reports/${reportId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total))
      }
    })
    return response.data.attachment
  },

  async deleteReportAttachment(_, { reportId, attachmentId }) {
    await http.delete(`/api/reports/${reportId}/attachments/${attachmentId}`)
  },

  async fetchPendingApprovals({ commit }) {
    try {
      const response = await http.get('/api/reports/dashboard/pending-approvals')
      commit('SET_PENDING_APPROVALS', response.data.items || [])
      return response.data.items || []
    } catch (error) {
      // 홈 카드용 - 조용히 실패
      return []
    }
  },

  async fetchMyReports({ commit }, { limit = 5 } = {}) {
    const response = await http.get('/api/reports/dashboard/my-reports', { params: { limit } })
    commit('SET_MY_REPORT_SUMMARY', response.data.summary)
    return response.data
  }
}

export const getters = {
  pendingApprovalCount: (state) => state.pendingApprovalCount
}
